import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { TriggerConditionConfigModel, StateMachineVariableConfig, VariableValueModel, TriggerConditionKind, StateMachineVariableType, StateMachineValueType, VariableModel } from '../../types';
import GuardianMessages from '../guardian-messages/guardian-messages.vue';

@Component({
    components: {
        GuardianMessages
    }
})
export default class TriggerConditionConfigComponent extends Vue {
    @Prop() condition!: TriggerConditionConfigModel;
    @Prop() variables!: StateMachineVariableConfig[];

    selectedVariableLeft: StateMachineVariableConfig | null = null;
    selectedVariableRight: StateMachineVariableConfig | null = null;

    valueTypeLeft: StateMachineValueType | null = null;
    valueTypeRight: StateMachineValueType | null = null;

    variablesLeft: VariableSelection[] = [];
    variablesRight: VariableSelection[] = [];

    isOpen: boolean = false;

    @Watch("condition.operandLeft")
    onVariableLeftChanged(newVariable: string) {
        const selectedVariable = this.variables.filter(variable => variable.name === newVariable)[0] ?? null;
        if (this.selectedVariableLeft !== selectedVariable) {
            this.selectedVariableLeft = selectedVariable;
            this.handleVariableChanged();
        }
    }

    @Watch("condition.operandRight")
    onVariableRightChanged(newVariable: string) {
        const selectedVariable = this.variables.filter(variable => variable.name === newVariable)[0] ?? null;
        if (this.selectedVariableRight !== selectedVariable) {
            this.selectedVariableRight = selectedVariable;
            this.handleVariableChanged();
        }
    }

    @Watch("condition.kind")
    onKindChanged(newKind: TriggerConditionKind) {
        this.handleConditionKindUpdate();
    }

    @Watch("valueTypeLeft")
    onValueTypeLeftChanged(newValueType: TriggerConditionKind | null) {
        this.variablesLeft = this.filterVariables(this.valueTypeLeft);

        if (this.selectedVariableLeft !== null) {
            const selectedVariableName = this.selectedVariableLeft.name;
            if (!this.variablesLeft.filter(v => v.variableName === selectedVariableName)[0]) {
                this.condition.operandLeft = "";
            } else {
                this.condition.operandLeft = selectedVariableName;
            }
        }
    }

    @Watch("valueTypeRight")
    onValueTypeRightChanged(newValueType: TriggerConditionKind | null) {
        this.variablesRight = this.filterVariables(this.valueTypeRight);

        if (this.selectedVariableRight !== null) {
            const selectedVariableName = this.selectedVariableRight.name;
            if (!this.variablesRight.filter(v => v.variableName === selectedVariableName)[0]) {
                this.condition.operandRight = "";
            } else {
                this.condition.operandRight = selectedVariableName;
            }
        }
    }

    @Watch("variables", { deep: true })
    onVariablesChanged() {
        this.onValueTypeLeftChanged(null);
        this.onValueTypeRightChanged(null);
    }

    mounted() {
        this.handleConditionKindUpdate();
        this.onValueTypeLeftChanged(this.valueTypeLeft);
        this.onValueTypeRightChanged(this.valueTypeRight);
        this.onVariableLeftChanged(this.condition.operandLeft);
        this.onVariableRightChanged(this.condition.operandRight);
    }

    handleConditionKindUpdate() {
        switch (this.condition.kind) {
            case TriggerConditionKind.DayOfMonth:
            case TriggerConditionKind.DayOfYear:
                this.valueTypeLeft = StateMachineValueType.Integer;
                this.valueTypeRight = StateMachineValueType.TimeZone;
                break;
            case TriggerConditionKind.DayOfWeek:
                this.valueTypeLeft = StateMachineValueType.String;
                this.valueTypeRight = StateMachineValueType.TimeZone;
                break;
            case TriggerConditionKind.TimeInState:
                this.valueTypeLeft = StateMachineValueType.TimeSpan;
                this.valueTypeRight = null; // Not used
                break;
            case TriggerConditionKind.TimeOfDay:
                this.valueTypeLeft = StateMachineValueType.TimeSpan;
                this.valueTypeRight = StateMachineValueType.TimeZone;
                break;
            case TriggerConditionKind.TimeOfYear:
                this.valueTypeLeft = StateMachineValueType.DateTime;
                this.valueTypeRight = StateMachineValueType.TimeZone;
                break;
            case TriggerConditionKind.VariableToVariable:
                this.valueTypeLeft = null; // Not used
                this.valueTypeRight = null; // Not used
                break;
            case TriggerConditionKind.VariableAgeToValue:
                this.valueTypeLeft = null; // Any type
                this.valueTypeRight = StateMachineValueType.TimeSpan;
        }
    }

    handleVariableChanged() {
        if (this.condition.kind == TriggerConditionKind.VariableToVariable) {
            if (this.selectedVariableLeft && this.selectedVariableRight === null) {
                this.valueTypeRight = this.selectedVariableLeft.valueType;
                this.valueTypeLeft = null;
            } else if (this.selectedVariableLeft === null && this.selectedVariableRight) {
                this.valueTypeLeft = this.selectedVariableRight.valueType;
                this.valueTypeRight = null;
            } else {
                this.valueTypeLeft = null;
                this.valueTypeRight = null;
            }
        }
    }

    filterVariables(valueType: StateMachineValueType | null): VariableSelection[] {
        return this.variables.filter(v => (valueType == null) || (v.valueType === valueType)).map(v => new VariableSelection(v.name, v.name));
    }

    operatorTexts = {
        "Less": "<",
        "LessEqual": "<=",
        "Greater": ">",
        "GreaterEqual": ">=",
        "Equal": "=",
        "NotEqual": "!="
    };
}

export class VariableSelection {
    title: string;
    variableName: string;

    constructor(title: string, variableName: string) {
        this.title = title;
        this.variableName = variableName;
    }
}
